import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Grid,
  Snackbar,
  Stack,
  Menu
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OpenIcon,
  Support as SupportIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { supportContractService } from '../services/supportContractService';
import { companyService } from '../services/companyService';
import { contactService } from '../services/contactService';
import { emailService } from '../services/emailService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import UserDisplay from '../components/UserDisplay';

const SupportContract = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isNewContract = contractId === 'new';

  // State
  const [contract, setContract] = useState({
    contractId: '',
    contractName: '',
    scopeOfSupport: '',
    companyId: '',
    companyName: '',
    contactIds: [],
    maxTicketsPerMonth: 1,
    status: 'Active',
    createdBy: '',
    createdDate: new Date().toISOString(),
    updatedBy: '',
    updatedDate: null
  });

  // Data loading state
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Ticket management state
  const [tickets, setTickets] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketFormData, setTicketFormData] = useState({
    title: '',
    ticketDetail: '',
    status: 'Open',
    createdDate: new Date().toISOString().split('T')[0] // Default to today's date in YYYY-MM-DD format
  });
  
  // Ticket deletion confirmation state
  const [ticketDeleteDialogOpen, setTicketDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);

  // Date range filter state
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [filteredTickets, setFilteredTickets] = useState([]);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  // Load companies and (optionally) all contacts on mount
  useEffect(() => {
    loadInitialData();
  }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter tickets based on date range
  useEffect(() => {
    const filtered = tickets.filter(ticket => {
      const ticketDate = new Date(ticket.createdDate || ticket.CreatedDate);
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      return ticketDate >= startDate && ticketDate <= endDate;
    });
    setFilteredTickets(filtered);
  }, [tickets, dateRange]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load companies
      const companiesData = await companyService.getCompanies();
      setCompanies(companiesData);
      
      // Load contract if editing
      if (!isNewContract) {
        const contractData = await supportContractService.getSupportContractById(contractId);
        console.log('Loaded contract data:', contractData); // Debug log
        
        // Set the contract data
        setContract({
          ...contractData,
          contactIds: contractData.contacts?.map(c => c.contactId) || []
        });
        
        // Set tickets
        setTickets(contractData.tickets || []);
        
        // Load contacts for the contract's company
        if (contractData.companyId) {
          const companyContacts = await contactService.getContactsByCompany(contractData.companyId);
          setAvailableContacts(companyContacts);
          setContacts(companyContacts);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch contacts for the selected company when companyId changes
  useEffect(() => {
    const fetchContacts = async () => {
      if (contract.companyId) {
        try {
          setLoading(true);
          const companyContacts = await contactService.getContactsByCompany(contract.companyId);
          setAvailableContacts(companyContacts);
        } catch (err) {
          setAvailableContacts([]);
          setError('Failed to load contacts for selected company');
        } finally {
          setLoading(false);
        }
      } else {
        setAvailableContacts([]);
      }
    };
    fetchContacts();
  }, [contract.companyId]);

  const getCompanyName = useCallback((companyId) => {
    const company = companies.find(c => c.companyId === companyId);
    return company ? company.companyName : '';
  }, [companies]);

  const getContactDisplayName = useCallback((contact) => {
    const fullName = `${contact.firstName} ${contact.lastName}`;
    if (contract.companyId) {
      return fullName; // If company is selected, just show name
    } else {
      const companyName = getCompanyName(contact.companyId);
      return companyName ? `${fullName} - ${companyName}` : fullName;
    }
  }, [contract.companyId, getCompanyName]);

  const handleContractChange = (field) => (event) => {
    const value = event.target.value;
    setContract(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear contact selection if company changes
    if (field === 'companyId') {
      setContract(prev => ({
        ...prev,
        contactIds: []
      }));
    }
  };

  const handleContactsChange = (event, newValue) => {
    setContract(prev => ({
      ...prev,
      contactIds: newValue.map(contact => contact.contactId)
    }));
  };

  const handleSaveContract = async () => {
    // Validation
    if (!contract.contractName.trim()) {
      setError('Contract name is required');
      return;
    }
    if (!contract.scopeOfSupport.trim()) {
      setError('Scope of support is required');
      return;
    }
    if (!contract.contactIds.length) {
      setError('At least one contact is required');
      return;
    }
    if (!contract.maxTicketsPerMonth || parseInt(contract.maxTicketsPerMonth) <= 0) {
      setError('Max tickets per month must be a positive number');
      return;
    }

    setSaving(true);
    try {
      const contractData = {
        ...contract,
        maxTicketsPerMonth: parseInt(contract.maxTicketsPerMonth),
        tickets: tickets,
        createdBy: isNewContract ? currentUser.email : contract.createdBy
      };

      await supportContractService.saveSupportContract(contractData, currentUser.email);
      setSuccess('Support contract saved successfully');
      
      if (isNewContract) {
        // Navigate to the contract list after creating
        setTimeout(() => navigate('/support-contracts'), 1500);
      }
    } catch (err) {
      console.error('Error saving contract:', err);
      setError(`Failed to save contract: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Ticket management functions
  const getTicketsForCurrentMonth = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return tickets.filter(ticket => {
      const ticketDate = new Date(ticket.createdDate);
      return ticketDate.getMonth() === currentMonth && ticketDate.getFullYear() === currentYear;
    }).length;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Since we now normalize dates in the service layer to YYYY-MM-DD format,
    // we can safely create a local date object
    const [year, month, day] = dateString.split('-');
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get contact emails for the contract
  const getContactEmails = () => {
    console.log('=== getContactEmails called ===');
    console.log('Selected contacts:', selectedContacts);
    
    const emails = selectedContacts
      .filter(contact => {
        const hasEmail = contact.email || contact.Email;
        console.log(`Contact ${contact.firstName} ${contact.lastName}: ${hasEmail ? 'has email' : 'no email'}`);
        return hasEmail;
      })
      .map(contact => {
        const email = contact.email || contact.Email;
        console.log(`Mapped email for ${contact.firstName} ${contact.lastName}: ${email}`);
        return email;
      });
      
    console.log('Final email list:', emails);
    return emails;
  };

  // Helper function to determine ticket action for email notifications
  const getTicketAction = (oldStatus, newStatus, isNewTicket) => {
    console.log('=== getTicketAction called ===');
    console.log('Parameters:', { oldStatus, newStatus, isNewTicket });
    
    let action = null;
    
    // For new tickets
    if (isNewTicket) {
      action = newStatus === 'Closed' ? 'created and completed' : 'created';
      console.log('New ticket action:', action);
      return action;
    }
    
    // For existing tickets - only notify when closing the ticket
    if (oldStatus === 'Open' && newStatus === 'Closed') {
      action = 'completed';
      console.log('Existing ticket action:', action);
      return action;
    }
    
    console.log('No notification action needed');
    return null;
  };

  const sendTicketNotification = async (ticketData, ticketAction) => {
    try {
      const contactEmails = getContactEmails();
      
      if (contactEmails.length === 0) {
        return;
      }

      const result = await emailService.sendTicketNotification(
        ticketData,
        contract,
        contactEmails,
        ticketAction
      );

      if (result.success) {
        setSuccess(prev => `${prev} (Email sent to ${result.recipients.length} contact(s))`);
      } else {
        console.error('Email notification failed:', result.message);
      }
    } catch (error) {
      console.error('Error in sendTicketNotification:', error);
    }
  };

  // Auto-save function for ticket changes (without validation for contract fields)
  const autoSaveContract = async (updatedTickets = tickets) => {
    try {
      const contractData = {
        ...contract,
        maxTicketsPerMonth: parseInt(contract.maxTicketsPerMonth),
        tickets: updatedTickets,
        createdBy: isNewContract ? currentUser.email : contract.createdBy
      };

      await supportContractService.saveSupportContract(contractData, currentUser.email);
      console.log('Contract auto-saved successfully');
    } catch (err) {
      console.error('Error auto-saving contract:', err);
      // Don't show error to user for auto-save failures
    }
  };

  const selectedContacts = availableContacts.filter(contact => 
    contract.contactIds.includes(contact.contactId)
  );

  // Export menu handlers
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportClick = (format) => {
    handleExportMenuClose();
    if (format === 'excel') {
      exportToExcel();
    } else if (format === 'pdf') {
      exportToPDF();
    }
  };

  // Export functions
  const exportToExcel = async () => {
    const ExcelJS = require('exceljs');
    
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Support Tickets');

    // Define columns with widths
    worksheet.columns = [
      { header: 'Title', key: 'title', width: 35 },
      { header: 'Ticket Detail', key: 'ticketDetail', width: 90 },
      { header: 'Created Date', key: 'createdDate', width: 25 },
      { header: 'Status', key: 'status', width: 15 }
    ];

    // Style the header row
    worksheet.getRow(1).height = 30;
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4285F4' }
    };
    worksheet.getRow(1).alignment = { 
      vertical: 'middle', 
      horizontal: 'center',
      wrapText: true
    };
    worksheet.getRow(1).border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Add data rows
    filteredTickets.forEach((ticket, index) => {
      const row = worksheet.addRow({
        title: ticket.title || ticket.Title || '',
        ticketDetail: ticket.ticketDetail || ticket.TicketDetail || '',
        createdDate: formatDate(ticket.createdDate || ticket.CreatedDate),
        status: ticket.status || ticket.Status
      });

      // Calculate row height based on content length and line wrapping
      const ticketDetailText = (ticket.ticketDetail || ticket.TicketDetail || '');
      const titleText = (ticket.title || ticket.Title || '');
      
      // Count manual line breaks
      const ticketDetailLineBreaks = (ticketDetailText.match(/\n/g) || []).length;
      const titleLineBreaks = (titleText.match(/\n/g) || []).length;
      
      // Calculate lines needed for each column (more conservative estimate)
      // Account for both text wrapping and manual line breaks
      const ticketDetailLines = Math.max(
        ticketDetailLineBreaks + 1,
        Math.ceil(ticketDetailText.length / 70) // ~70 chars per line (more conservative)
      );
      const titleLines = Math.max(
        titleLineBreaks + 1,
        Math.ceil(titleText.length / 25) // ~25 chars per line (more conservative)
      );
      
      // Take the maximum lines needed
      const maxLines = Math.max(ticketDetailLines, titleLines, 1);
      
      // Calculate row height with more generous spacing
      // Base height + (lines * height per line) with padding
      const rowHeight = Math.max(40, Math.min(400, 25 + (maxLines * 20)));
      row.height = rowHeight;

      // Apply styling to each cell in the row
      row.eachCell((cell, colNumber) => {
        // Word wrap for all cells
        cell.alignment = {
          vertical: 'top',
          horizontal: colNumber === 2 ? 'left' : 'center', // Ticket Detail left-aligned
          wrapText: true
        };

        // Borders
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        };

        // Font
        cell.font = { size: 10 };

        // Alternate row colors
        if ((index + 2) % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' }
          };
        }
      });
    });

    // Generate filename with date range
    const startDate = new Date(dateRange.startDate).toLocaleDateString('en-US');
    const endDate = new Date(dateRange.endDate).toLocaleDateString('en-US');
    const filename = `Support_Tickets_${startDate.replace(/\//g, '-')}_to_${endDate.replace(/\//g, '-')}.xlsx`;

    // Write to buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    
    setSuccess('Excel file exported successfully');
  };

  const exportToPDF = () => {
    import('jspdf').then(({ jsPDF }) => {
      import('jspdf-autotable').then((autoTable) => {
        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table display
        
        // Add title
        doc.setFontSize(16);
        doc.text('Support Tickets Report', 14, 22);
        
        // Add date range
        doc.setFontSize(10);
        const startDate = new Date(dateRange.startDate).toLocaleDateString('en-US');
        const endDate = new Date(dateRange.endDate).toLocaleDateString('en-US');
        doc.text(`Date Range: ${startDate} - ${endDate}`, 14, 30);
        
        // Add contract name
        doc.text(`Contract: ${contract.contractName}`, 14, 36);
        
        // Prepare table data
        const tableData = filteredTickets.map(ticket => [
          ticket.title || ticket.Title,
          ticket.ticketDetail || ticket.TicketDetail, // Full text without truncation
          formatDate(ticket.createdDate || ticket.CreatedDate),
          ticket.status || ticket.Status
        ]);

        // Add table using the autoTable plugin
        autoTable.default(doc, {
          head: [['Title', 'Ticket Detail', 'Created Date', 'Status']],
          body: tableData,
          startY: 45,
          styles: { 
            fontSize: 8,
            cellPadding: 3,
            overflow: 'linebreak',
            halign: 'left'
          },
          columnStyles: {
            0: { cellWidth: 50, halign: 'left' }, // Title column - wider in landscape
            1: { cellWidth: 120, halign: 'left', overflow: 'linebreak' }, // Ticket Detail column - much wider with text wrapping
            2: { cellWidth: 35, halign: 'center' }, // Created Date column
            3: { cellWidth: 25, halign: 'center' }  // Status column
          },
          headStyles: {
            fontSize: 9,
            fontStyle: 'bold',
            fillColor: [66, 139, 202], // Blue header background
            textColor: [255, 255, 255] // White text
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245] // Light gray for alternate rows
          },
          margin: { left: 10, right: 10 },
          pageBreak: 'auto',
          tableWidth: 'wrap'
        });

        // Generate filename with date range
        const filename = `Support_Tickets_${startDate.replace(/\//g, '-')}_to_${endDate.replace(/\//g, '-')}.pdf`;

        // Download file
        doc.save(filename);
        setSuccess('PDF file exported successfully');
      }).catch(error => {
        console.error('Error loading jspdf-autotable:', error);
        setError('Failed to export PDF. Please try again.');
      });
    }).catch(error => {
      console.error('Error loading jspdf:', error);
      setError('Failed to export PDF. Please try again.');
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/support-contracts')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <SupportIcon sx={{ mr: 2 }} />
            {isNewContract ? 'Create Support Contract' : 'Edit Support Contract'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveContract}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Contract'}
        </Button>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Contract Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>Contract Details</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contract Name *"
              value={contract.contractName}
              onChange={handleContractChange('contractName')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Scope of Support *"
              multiline
              rows={3}
              value={contract.scopeOfSupport}
              onChange={handleContractChange('scopeOfSupport')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={contract.companyId}
                label="Company"
                onChange={handleContractChange('companyId')}
              >
                <MenuItem value="">
                  <em>No Company Selected</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Tickets per Month *"
              type="number"
              value={contract.maxTicketsPerMonth}
              onChange={handleContractChange('maxTicketsPerMonth')}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={contract.status}
                label="Status"
                onChange={handleContractChange('status')}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={availableContacts}
              getOptionLabel={(option) => getContactDisplayName(option)}
              value={selectedContacts}
              onChange={handleContactsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contact List *"
                  placeholder="Select contacts"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.contactId}
                    label={getContactDisplayName(option)}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Grid>
        </Grid>

        {/* Created Info */}
        {!isNewContract && contract.createdBy && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Created by:
                </Typography>
                <UserDisplay email={contract.createdBy} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Created on:
                </Typography>
                <Typography variant="body2">
                  {formatDate(contract.createdDate)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Tickets Section */}
      {!isNewContract && (
        <Paper sx={{ p: 3 }}>
          {/* Tickets Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h6">Support Tickets</Typography>
              <Typography variant="body2" color="text.secondary">
                Tickets for this month: {getTicketsForCurrentMonth()}/{contract.maxTicketsPerMonth}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingTicket(null);
                setTicketFormData({
                  title: '',
                  ticketDetail: '',
                  status: 'Open',
                  createdDate: new Date().toISOString().split('T')[0] // Default to today's date
                });
                setTicketModalOpen(true);
              }}
            >
              Create Support Ticket
            </Button>
          </Box>

          {/* Date Range Filter and Export Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ minWidth: 'fit-content' }}>
                  Filter by Date Range:
                </Typography>
                <TextField
                  label="Start Date"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 150 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredTickets.length} ticket(s)
                </Typography>
              </Stack>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportMenuOpen}
                  disabled={filteredTickets.length === 0}
                  size="medium"
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={exportMenuOpen}
                  onClose={handleExportMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={() => handleExportClick('excel')}>
                    <ExcelIcon sx={{ mr: 1 }} />
                    Export to Excel
                  </MenuItem>
                  <MenuItem onClick={() => handleExportClick('pdf')}>
                    <PdfIcon sx={{ mr: 1 }} />
                    Export to PDF
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Paper>

          {/* Basic Tickets Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Ticket Detail</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center" width={120}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No tickets found in the selected date range
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id || ticket.Id} hover>
                      <TableCell>{ticket.title || ticket.Title}</TableCell>
                      <TableCell>{ticket.ticketDetail || ticket.TicketDetail}</TableCell>
                      <TableCell>{formatDate(ticket.createdDate || ticket.CreatedDate)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(ticket.status || ticket.Status) === 'Closed' ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : (
                            <OpenIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingTicket(ticket);
                            setTicketFormData({
                              title: ticket.title || ticket.Title,
                              ticketDetail: ticket.ticketDetail || ticket.TicketDetail,
                              status: ticket.status || ticket.Status,
                              createdDate: ticket.createdDate || ticket.CreatedDate ? 
                                (() => {
                                  const dateStr = ticket.createdDate || ticket.CreatedDate;
                                  // If it's a full datetime string, extract just the date part
                                  if (dateStr.includes('T')) {
                                    return dateStr.split('T')[0];
                                  }
                                  // If it's already a date string, use it as is
                                  return dateStr;
                                })() : 
                                new Date().toISOString().split('T')[0]
                            });
                            setTicketModalOpen(true);
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTicketToDelete(ticket);
                            setTicketDeleteDialogOpen(true);
                          }}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Ticket Modal */}
      <Dialog 
        open={ticketModalOpen} 
        onClose={() => {
          setTicketModalOpen(false);
          setEditingTicket(null);
          setTicketFormData({
            title: '',
            ticketDetail: '',
            status: 'Open',
            createdDate: new Date().toISOString().split('T')[0] // Default to today's date
          });
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingTicket ? 'Edit Support Ticket' : 'Create Support Ticket'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title *"
              value={ticketFormData.title}
              onChange={(e) => setTicketFormData(prev => ({ ...prev, title: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Ticket Detail *"
              multiline
              rows={4}
              value={ticketFormData.ticketDetail}
              onChange={(e) => setTicketFormData(prev => ({ ...prev, ticketDetail: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={ticketFormData.status}
                label="Status"
                onChange={(e) => setTicketFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Creation Date *"
              type="date"
              value={ticketFormData.createdDate}
              onChange={(e) => setTicketFormData(prev => ({ ...prev, createdDate: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTicketModalOpen(false);
            setEditingTicket(null);
            setTicketFormData({
              title: '',
              ticketDetail: '',
              status: 'Open',
              createdDate: new Date().toISOString().split('T')[0] // Default to today's date
            });
          }}>Cancel</Button>
          <Button 
            onClick={async () => {
              console.log('=== Starting ticket update/create flow ===');
              if (!ticketFormData.title.trim() || !ticketFormData.ticketDetail.trim() || !ticketFormData.createdDate) {
                setError('Title, ticket detail, and creation date are required');
                return;
              }
              let updatedTickets;
              if (editingTicket) {
                // Update existing ticket in DB
                const oldStatus = editingTicket.status || editingTicket.Status;
                const newStatus = ticketFormData.status;
                console.log('Ticket status change detected:', { 
                  oldStatus, 
                  newStatus,
                  ticketId: editingTicket.id || editingTicket.Id,
                  title: ticketFormData.title
                });

                const updatedTicket = {
                  ...editingTicket,
                  ...ticketFormData
                };
                console.log('Updating ticket in DB:', updatedTicket);

                try {
                  await supportContractService.updateSupportTicket(editingTicket.id || editingTicket.Id, {
                    title: ticketFormData.title,
                    ticketDetail: ticketFormData.ticketDetail,
                    status: ticketFormData.status,
                    createdDate: ticketFormData.createdDate
                  });
                  console.log('Successfully updated ticket in DB');
                } catch (error) {
                  console.error('Failed to update ticket in DB:', error);
                  setError('Failed to update ticket');
                  return;
                }

                updatedTickets = tickets.map(ticket => 
                  (ticket.id || ticket.Id) === (editingTicket.id || editingTicket.Id)
                    ? { ...updatedTicket }
                    : ticket
                );
                setTickets(updatedTickets);
                setSuccess('Ticket updated successfully');

                // Check if notification is needed
                console.log('Checking if notification is needed...');
                const ticketAction = getTicketAction(oldStatus, newStatus, false);
                console.log('Ticket action determined:', ticketAction);

                if (ticketAction) {
                  console.log('Notification needed. Preparing to send notification...');
                  console.log('Updated ticket data:', updatedTicket);
                  console.log('Contract data:', contract);
                  console.log('Selected contacts:', selectedContacts);
                  
                  try {
                    await sendTicketNotification(updatedTicket, ticketAction);
                    console.log('Notification sent successfully');
                  } catch (error) {
                    console.error('Failed to send notification:', error);
                  }
                } else {
                  console.log('No notification needed for this status change');
                }

                console.log('=== Completed ticket update flow ===');
              } else {
                // Add new ticket...
                console.log('=== Starting new ticket creation flow ===');
                const newTicket = {
                  ...ticketFormData,
                  createdBy: currentUser.email
                };
                const createdTicket = await supportContractService.createSupportTicket(contract.contractId, newTicket);
                updatedTickets = [...tickets, createdTicket];
                setTickets(updatedTickets);
                setSuccess('Ticket created successfully');
                // Send notification for new ticket
                const ticketAction = getTicketAction(null, ticketFormData.status, true);
                await sendTicketNotification(createdTicket, ticketAction);
              }
              setTicketModalOpen(false);
              setEditingTicket(null);
              setTicketFormData({
                title: '',
                ticketDetail: '',
                status: 'Open',
                createdDate: new Date().toISOString().split('T')[0] // Default to today's date
              });
            }} 
            variant="contained"
          >
            {editingTicket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Delete Confirmation Dialog */}
      <Dialog
        open={ticketDeleteDialogOpen}
        onClose={() => setTicketDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Ticket Deletion"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this support ticket?
          </Typography>
          {ticketToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Title:</strong> {ticketToDelete.title || ticketToDelete.Title}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {ticketToDelete.status || ticketToDelete.Status}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(ticketToDelete.createdDate || ticketToDelete.CreatedDate)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTicketDeleteDialogOpen(false);
            setTicketToDelete(null);
          }}>Cancel</Button>
          <Button
            onClick={async () => {
              if (ticketToDelete) {
                await supportContractService.deleteSupportTicket(ticketToDelete.id || ticketToDelete.Id);
                const updatedTickets = tickets.filter(t => (t.id || t.Id) !== (ticketToDelete.id || ticketToDelete.Id));
                setTickets(updatedTickets);
                setSuccess('Ticket deleted successfully');
                setTicketDeleteDialogOpen(false);
                setTicketToDelete(null);
              }
            }}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportContract;
